import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useLocation } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { usePageTitle } from '../../hooks/usePageTitle'
import { Edition } from '../../models/Edition'
import { Item } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { ItemSubType } from '../../types/models'
import { okResult } from '../../types/utils'
import { Step, navigateToNextDefaultStep } from '../../utils/defaults'
import { plural } from '../../utils/item'
import { getEditionID } from '../../utils/users'
import { PSApplication } from '../app/PSApplication'
import { ItemsPage, itemTitleAndHelperText } from '../items/ItemsPage'
import { WorkflowSelectListSkeleton } from '../common/skeletons/WorkflowSelectListSkeleton'

const editionDefaultsForType = (type: ItemSubType, edition?: Edition) => {
    if (!edition) return []

    switch (type) {
        case 'service':
            return edition.defaultServices
        case 'product':
            return edition.defaultProducts
        case 'class':
            return edition.defaultClasses
        case 'reservation':
            return edition.defaultReservations
        case 'package':
            return edition.defaultPackages
        case 'subscription':
            return edition.defaultSubscriptions
        case 'gift':
            return edition.defaultGifts
        default:
            return []
    }
}

type Props = {
    type: ItemSubType
}

export function SetupDefaultItemsPage({ type }: Props) {
    const me = PSApplication.me()
    const owner = PSApplication.owner()
    const editionID = getEditionID(me)
    const [searchParams] = useSearchParams()
    const location = useLocation()

    const { title, helperText } = itemTitleAndHelperText(type)

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const lastHistoryEntry = findLastHistoryEntry({ excluding: /^\/settings\/.*\/.+/ })

    usePageTitle(title)

    const navigate = usePSNavigate()

    const queryClient = useQueryClient()
    const { data, isLoading } = useQuery({
        queryKey: ['defaults', location.pathname, editionID],
        queryFn: async () => {
            const recordIDs = searchParams.get('recordIDs')?.split(',') ?? []
            if (recordIDs.length > 0) {
                const edition = await RecordFactory.getInstance().loadEdition(editionID)
                const defaultItems = editionDefaultsForType(type, edition)

                return {
                    itemsToSelect: defaultItems
                        .filter((i) => recordIDs.includes(i.id ?? ''))
                        .map((i) => ({
                            item: i,
                            selected: true,
                        })),
                    edition,
                }
            }

            const [edition, installedItems] = await Promise.all([
                RecordFactory.getInstance().loadEdition(getEditionID(me)),
                RecordFactory.getInstance().loadItems(owner.id!, type),
            ])

            const defaultItems = editionDefaultsForType(type, edition)

            const installedItemsFromSameEdition = installedItems.filter(
                (i) => i.edition === editionID
            )

            const itemsToSelect = defaultItems
                .filter(
                    (i) =>
                        installedItemsFromSameEdition.find(
                            (saved) => saved.editionItem === i.id
                        ) === undefined
                )
                .map((i) => ({
                    item: i,
                    selected: false,
                }))

            return {
                type,
                itemsToSelect,
                edition,
            }
        },
    })

    const onNextHandler = useCallback(
        async (items: Item[]) => {
            if (!data?.edition) {
                return okResult(null)
            }

            // Send the new items with the proper IDs set
            const clonedItems = items.map((item) => {
                const clone = item.clone()
                clone.edition = editionID
                clone.editionItem = item.id
                clone.id = undefined
                return clone
            })

            const result = await RecordFactory.getInstance().sendOnboardingItemsSetup(
                type,
                clonedItems,
                editionID,
                false
            )
            if (!result.ok) {
                return result
            }

            queryClient.invalidateQueries({
                queryKey: ['defaults'],
            })

            let initialStep = searchParams?.get('initialStep') as Step | null
            if (!initialStep) {
                initialStep = plural(type) as Step
            }

            const potentialItemIDs = searchParams.get('recordIDs')?.split(',') ?? []

            const potentialFormsAndContracts = clonedItems
                .flatMap((i) => [i.onboardingFormID, i.onboardingContractID])
                .filter(Boolean) as string[]

            await navigateToNextDefaultStep(navigate, {
                edition: data.edition!,
                potentialItemIDs: [...potentialItemIDs, ...potentialFormsAndContracts],
                step: plural(type) as Step,
                initialStep,
            })

            return okResult(null)
        },
        [navigate, type, editionID, searchParams, data]
    )

    if (!data || isLoading) {
        return <WorkflowSelectListSkeleton title={title} subtitle={helperText} onClose={() => {}} />
    }

    return (
        <ItemsPage
            edition={data.edition}
            items={data.itemsToSelect}
            itemType={type}
            onSave={onNextHandler}
            onBack={{
                title: lastHistoryEntry?.title,
                onClick: () => {
                    if (lastHistoryEntry) {
                        lastHistoryEntry.goBackTo()
                    }
                    else {
                        navigate('/')
                    }
                },
            }}
            onClose={() => {
                if (lastHistoryEntry) {
                    lastHistoryEntry.goBackTo()
                }
                else {
                    navigate('/')
                }
            }}
        />
    )
}
